import * as t from 'io-ts';
import { isUndefined } from 'lodash-es';

import type {
    DatabricksConditionInputDefinition,
    InteractiveTitleNumberInputDefinition,
    InteractiveTitleSelectDefinition,
    InteractiveTitleSettingDefinition,
} from 'header/types';
import type { DatabricksOrder} from 'portlet/models/query';
import { isDatabricksQuery } from 'portlet/models/query';

import { createSpecSettingsInterface, define } from './_common';

export interface DatabricksConditionSettingDefinition extends DatabricksConditionInputDefinition {
    key: 'DATABRICKS_CONDITION';
}

const definition: DatabricksConditionSettingDefinition = {
    key: 'DATABRICKS_CONDITION',
    type: 'DatabricksCondition',
    label: 'DatabricksCondition',
    value: { field: '', order: 'ASC', limit: 1000 },
    condtitionFieldOptions: [],
    conditionAllowChangeOrder: true,
    conditionMaxLimit: 1000,
};

const specSettingsInterface = createSpecSettingsInterface(
    definition,
    t.type({
        field: t.string,
        order: t.union([t.literal('ASC'), t.literal('DESC')]),
        limit: t.number,
    }),
);

export const codec = specSettingsInterface.input;

export const DatabricksConditionSettingDefinition = define({
    key: 'DATABRICKS_CONDITION',
    label: 'dbxCondition',
    definition,
    fromSetting: settings => specSettingsInterface.extractValue(settings.dbxCondition),
    createDefinitionWithOpts: (opts = {}) => ({ ...definition, ...opts }),
    createDefinitionFromSpec: (settings, spec, view) => {
        const query = view?.query || spec.query;

        if (isUndefined(settings.dbxDrill) || !query || !isDatabricksQuery(query)) {
            return;
        }
        const narrowedFields =
            query.condition && !isUndefined(query.condition.fields) ? query.condition.fields : [];

        return {
            ...definition,
            ...specSettingsInterface.createDefinitionWithSettings(settings.dbxCondition),
            conditionMaxLimit: query.condition?.maxLimit ?? 1000,
            conditionAllowChangeOrder: query.condition?.allowChangeOrder ?? true,
            condtitionFieldOptions: query.tables
                .map(item => item.columns)
                .flat()
                .filter(item =>
                    narrowedFields.length === 0 ? true : narrowedFields.includes(item.name),
                )
                .map(item => ({ key: item.name, label: item.name })),
        };
    },

    replacementTags: definition => ({
        DBX_CONDITION_LIMIT: definition.value.limit.toString(),
        DBX_CONDITION_FIELD: definition.value.field,
        DBX_CONDITION_ORDER: definition.value.order,
    }),

    interactiveTags: definition => {
        const orderOptions = [
            { key: 'ASC', label: 'ASC' },
            { key: 'DESC', label: 'DESC' },
        ];
        const limitTag: InteractiveTitleSettingDefinition<
            DatabricksConditionSettingDefinition,
            InteractiveTitleNumberInputDefinition
        > = {
            setting: definition,
            field: {
                type: 'Number',
                value:
                    definition.value.limit > definition.conditionMaxLimit
                        ? definition.conditionMaxLimit
                        : definition.value.limit,
                min: 1,
                max: definition.conditionMaxLimit,
                step: 1,
            },
            format: limit => ({ ...definition, value: { ...definition.value, limit } }),
        };

        const fieldTag: InteractiveTitleSettingDefinition<
            DatabricksConditionSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: definition.value.field,
                options: definition.condtitionFieldOptions,
            },
            format: field => ({
                ...definition,
                value: { ...definition.value, field },
            }),
        };
        const orderTag: InteractiveTitleSettingDefinition<
            DatabricksConditionSettingDefinition,
            InteractiveTitleSelectDefinition
        > = {
            setting: definition,
            field: {
                type: 'Select',
                value: definition.value.order,
                options: orderOptions.filter(item =>
                    !definition.conditionAllowChangeOrder
                        ? item.key === definition.value.order
                        : true,
                ),
            },
            format: order => ({
                ...definition,
                value: { ...definition.value, order: order as DatabricksOrder },
            }),
        };

        return {
            DBX_CONDITION_LIMIT: limitTag,
            DBX_CONDITION_FIELD: fieldTag,
            DBX_CONDITION_ORDER: orderTag,
        };
    },
});
